import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { LuMail } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import { GLASS_CHIP } from '../../../components/ui/glass';
import { ERROR_RING, Field } from '../../../components/form/FormControls';
import { useTranslation } from '../../../i18n/LanguageContext';
import {
  COMPANY_EMAIL_DOMAIN,
  companyEmail,
  isCompanyEmail,
  isValidLocalPart,
  localPartOf,
  normalizeLocalPart,
} from './employeeFormOptions';

// The shared controls, re-exported for the employee forms
export { Field, INPUT_CLASS, SelectInput, TextArea, TextInput, ToggleTile } from '../../../components/form/FormControls';

/**
 * Company e-mail: the user types only the part before the @; the domain is a
 * fixed suffix and the full address goes to `onChange`. With
 * `allowExternal`, an existing address on another domain is shown as it is
 * (and kept) until the user switches it to the company domain.
 */
export const CompanyEmailField = ({ id, label, value, onChange, error, allowExternal }) => {
  const { t } = useTranslation();
  const [external, setExternal] = useState(() => Boolean(allowExternal && value && !isCompanyEmail(value)));
  const [blurred, setBlurred] = useState(false);
  const local = localPartOf(value);
  const ownError = blurred && local && !isValidLocalPart(local) ? t('employees.form.email.invalid') : '';
  const shownError = error || ownError;

  if (external) {
    return (
      <Field label={label} htmlFor={id} hint={t('employees.form.email.externalHint')} className="sm:tw-col-span-2">
        <div className={`tw-flex tw-h-10 tw-items-center tw-gap-2.5 tw-rounded-xl tw-px-3.5 ${GLASS_CHIP}`}>
          <LuMail aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
          <span id={id} className="tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-text-slate-800 dark:tw-text-slate-100">{value}</span>
          <Badge tone="warning" className="tw-hidden sm:tw-inline-flex">{t('employees.form.email.external')}</Badge>
        </div>
        <button
          type="button"
          onClick={() => {
            setExternal(false);
            onChange(companyEmail(normalizeLocalPart(localPartOf(value))));
          }}
          className="tw-rounded-full tw-bg-transparent tw-px-0.5 tw-text-xs tw-font-semibold tw-text-teal-700 tw-underline-offset-2 hover:tw-underline focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-text-teal-300"
        >
          {t('employees.form.email.convert', { domain: COMPANY_EMAIL_DOMAIN })}
        </button>
      </Field>
    );
  }

  return (
    <Field
      label={label}
      htmlFor={id}
      error={shownError}
      hint={local ? t('employees.form.email.preview', { email: companyEmail(local) }) : t('employees.form.email.hint')}
      className="sm:tw-col-span-2"
    >
      <div
        className={`tw-flex tw-h-10 tw-items-stretch tw-overflow-hidden tw-rounded-xl tw-transition-shadow focus-within:tw-ring-2 focus-within:tw-ring-teal-500/40 ${GLASS_CHIP} ${shownError ? ERROR_RING : ''}`}
      >
        <input
          id={id}
          type="text"
          inputMode="email"
          autoComplete="off"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={local}
          placeholder={t('employees.form.email.placeholder')}
          aria-invalid={shownError ? true : undefined}
          onChange={(event) => onChange(companyEmail(normalizeLocalPart(event.target.value)))}
          onBlur={() => setBlurred(true)}
          className="tw-min-w-0 tw-flex-1 tw-border-0 tw-bg-transparent tw-px-3.5 tw-text-base tw-text-slate-800 tw-shadow-none tw-outline-none placeholder:tw-text-slate-400 sm:tw-text-sm dark:tw-text-slate-100 dark:placeholder:tw-text-slate-500"
        />
        <span
          aria-hidden="true"
          className="tw-flex tw-shrink-0 tw-items-center tw-border-l tw-border-slate-200/80 tw-bg-slate-900/[0.035] tw-px-3 tw-text-sm tw-font-semibold tw-text-slate-500 dark:tw-border-white/10 dark:tw-bg-white/[0.05] dark:tw-text-slate-400"
        >
          @{COMPANY_EMAIL_DOMAIN}
        </span>
      </div>
    </Field>
  );
};

CompanyEmailField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  allowExternal: PropTypes.bool,
};
